import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import WorkflowStatusFilter from '@/components/WorkflowStatusChips';
import { productSchema } from '@/schemas';
import { PageCover, PageBody, CoverButton, CoverSearch, SheetHeader } from '@/components/Page';
import { formatBRL, formatBRLCompacto, formatNumero } from '@/lib/format';

const FILIAIS = ['Matriz', 'Filial 2', 'Filial 5', 'Filial SP', 'Filial ES'];
const ROTULO_CURTO = { Matriz: 'Matriz', 'Filial 2': '2', 'Filial 5': '5', 'Filial SP': 'SP', 'Filial ES': 'ES' };

// Filtro no padrão aprovado (botão Filtrar + chip removível): onde há estoque, ou nada em estoque.
const FILTROS = {
  ...Object.fromEntries(FILIAIS.map((f) => [f, { label: `Com estoque na ${f}`, color: 'bg-blue-500' }])),
  sem_estoque: { label: 'Sem estoque', color: 'bg-gray-400' },
};

// A tabela cresce aos poucos ("Mostrar mais"): o catálogo tem milhares de itens e a busca é o caminho normal.
const LOTE_LISTA = 50;
// O catálogo inteiro vem numa consulta (4.289 SKUs em 30/09/2026); folga larga e aviso se um dia estourar.
const LIMITE_CATALOGO = 20000;

const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const qtd = (p, filial) => Number(p.estoques?.[filial]) || 0;
// Estoque negativo existe (venda sem saldo no Tiny): conta como sem estoque em todo lugar.
const temEstoque = (p) => Number(p.estoque_total) > 0;
const tinyIdsTexto = (p) =>
  FILIAIS.map((f) => {
    const v = p.tiny_ids?.[f];
    return v && String(v) !== '0' ? `${f}: ${v}` : null;
  }).filter(Boolean).join('\n');
const corQtd = (q) => (q < 0 ? 'text-red-700 dark:text-red-300' : q ? '' : 'text-gray-500');
// A listagem (Postgres) manda o custo médio em `custo`; `custo_medio` é o nome do campo no formulário.
const custoDe = (p) => Number(p.custo ?? p.custo_medio) || 0;
const margemDe = (p) => {
  const preco = Number(p.preco) || 0;
  const custo = custoDe(p);
  return custo > 0 && preco > 0 ? Math.round(((preco - custo) / preco) * 100) : null;
};

// Zero vira traço: a tabela fica quieta e o número que existe salta aos olhos.
const Traco = () => (
  <>
    <span aria-hidden>–</span>
    <span className="sr-only">0</span>
  </>
);

const PRODUCT_FORM_DEFAULTS = {
  tiny_id: '',
  tiny_id_matriz: '',
  tiny_id_filial2: '',
  tiny_id_filial5: '',
  tiny_id_filialSP: '',
  tiny_id_filialES: '',
  sku: '',
  nome: '',
  preco: '',
  custo_medio: '',
  unidade: 'UN',
  peso_bruto: '',
  peso_liquido: '',
  largura: '',
  altura: '',
  comprimento: '',
  categoria: '',
  descricao: '',
  imagem_url: '',
  estoquematriz: '',
  estoque2: '',
  estoque5: '',
  estoqueSP: '',
  estoqueES: '',
};

const FieldError = ({ error }) =>
  error ? (
    <p className="text-xs text-red-600 dark:text-red-400" role="alert">
      {error.message}
    </p>
  ) : null;

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(false);
  // Foco volta para quem abriu: o nome na tabela (ao fechar a edição) e o Excluir (ao cancelar a confirmação).
  const abridorRef = useRef(null);
  const excluirRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('all');
  const [limite, setLimite] = useState(LOTE_LISTA);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canEdit = user?.role === 'master';

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: PRODUCT_FORM_DEFAULTS,
    mode: 'onTouched',
  });

  // Carrega o catálogo inteiro uma vez; busca e filtro rodam aqui mesmo (antes cada letra
  // digitada refazia a consulta de até 5.000 produtos).
  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/produtos-postgres`, {
        params: { limit: LIMITE_CATALOGO },
      });
      setProducts(data);
      setErroCarga(false);
      if (data.length >= LIMITE_CATALOGO) {
        toast.warning(`O catálogo passou de ${formatNumero(LIMITE_CATALOGO)} produtos: a busca pode não achar todos.`);
      }
    } catch {
      setErroCarga(true);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  const recarregar = () => {
    setLoading(true);
    fetchProducts();
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // O sync roda em BACKGROUND no servidor (todas as filiais, ~50 páginas cada,
  // com pausa de rate-limit do Tiny — leva minutos). O botão só dispara e a
  // tela acompanha pelo status até concluir; aí recarrega a lista.
  const pollRef = useRef(null);
  const SYNC_TOAST = 'sync-catalogo';

  const pararPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const acompanharSync = useCallback(() => {
    pararPolling();
    setSyncing(true);
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/produtos/sync/status`);
        const last = data?.last || {};
        if (data?.running) {
          const onde = last.filial_atual
            ? `${last.filial_atual}, página ${last.pagina_atual || 1}`
            : 'iniciando…';
          toast.loading(`Sincronizando com o Tiny: ${onde} · ${last.produtos_gravados || 0} produtos`, { id: SYNC_TOAST });
          return;
        }
        pararPolling();
        setSyncing(false);
        if (last.status === 'erro') {
          toast.error(`Sincronização falhou: ${last.erro || 'erro desconhecido'}`, { id: SYNC_TOAST });
        } else {
          const aviso = last.api_errors ? ` (${last.api_errors} página(s) do Tiny não responderam)` : '';
          toast.success(`Catálogo sincronizado: ${last.produtos_gravados || 0} produtos${aviso}`, { id: SYNC_TOAST });
        }
        fetchProducts();
      } catch {
        pararPolling();
        setSyncing(false);
        toast.error('Não foi possível acompanhar a sincronização', { id: SYNC_TOAST });
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  }, [fetchProducts]);

  useEffect(() => pararPolling, []); // limpa o polling ao sair da página

  const handleSync = async () => {
    try {
      await axios.post(`${API_URL}/produtos/sync`, null);
      acompanharSync(); // já em andamento ou recém-iniciado: acompanha do mesmo jeito
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar a sincronização');
    }
  };

  const handleOpenAddDialog = () => {
    reset(PRODUCT_FORM_DEFAULTS);
    setAddDialogOpen(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    // A listagem vem do Postgres com chaves por RÓTULO de filial
    // (tiny_ids['Matriz'], estoques['Matriz']); produtos vindos do Mongo usariam
    // as chaves planas. Aceita as duas formas pra o form pré-preencher certo.
    const tiny_ids = product.tiny_ids || {};
    const estoques = product.estoques || {};
    // id_produto=0 é sentinela de "sem Tiny" — não mostra "0" no campo.
    const tid = (v) => (v && String(v) !== '0' ? String(v) : '');

    reset({
      tiny_id: product.tiny_id || '',
      tiny_id_matriz: tid(tiny_ids['Matriz'] ?? tiny_ids['1']),
      tiny_id_filial2: tid(tiny_ids['Filial 2'] ?? tiny_ids['2']),
      tiny_id_filial5: tid(tiny_ids['Filial 5'] ?? tiny_ids['3']),
      tiny_id_filialSP: tid(tiny_ids['Filial SP'] ?? tiny_ids['4']),
      tiny_id_filialES: tid(tiny_ids['Filial ES'] ?? tiny_ids['5']),
      sku: product.sku,
      nome: product.nome,
      preco: product.preco?.toString() || '',
      custo_medio: (product.custo_medio ?? product.custo)?.toString() || '',
      unidade: product.unidade || 'UN',
      peso_bruto: product.peso_bruto?.toString() || '',
      peso_liquido: product.peso_liquido?.toString() || '',
      largura: product.largura?.toString() || '',
      altura: product.altura?.toString() || '',
      comprimento: product.comprimento?.toString() || '',
      categoria: product.categoria || '',
      descricao: product.descricao || '',
      imagem_url: product.imagem_url || '',
      estoquematriz: (product.estoquematriz ?? estoques['Matriz'])?.toString() || '',
      estoque2: (product.estoque2 ?? estoques['Filial 2'])?.toString() || '',
      estoque5: (product.estoque5 ?? estoques['Filial 5'])?.toString() || '',
      estoqueSP: (product.estoqueSP ?? estoques['Filial SP'])?.toString() || '',
      estoqueES: (product.estoqueES ?? estoques['Filial ES'])?.toString() || '',
    });
    setEditDialogOpen(true);
  };

  const buildPayload = (data) => {
    const tiny_ids = {};
    if (data.tiny_id_matriz) tiny_ids['1'] = data.tiny_id_matriz;
    if (data.tiny_id_filial2) tiny_ids['2'] = data.tiny_id_filial2;
    if (data.tiny_id_filial5) tiny_ids['3'] = data.tiny_id_filial5;
    if (data.tiny_id_filialSP) tiny_ids['4'] = data.tiny_id_filialSP;
    if (data.tiny_id_filialES) tiny_ids['5'] = data.tiny_id_filialES;

    const parseFloatOrNull = (v) => (v !== '' && v != null ? parseFloat(v) : null);
    const parseIntOrZero = (v) => (v !== '' && v != null ? parseInt(v, 10) : 0);

    return {
      tiny_id: data.tiny_id || null,
      tiny_ids: Object.keys(tiny_ids).length > 0 ? tiny_ids : null,
      sku: data.sku,
      nome: data.nome,
      preco: parseFloat(data.preco),
      custo_medio: parseFloatOrNull(data.custo_medio),
      unidade: data.unidade,
      peso_bruto: parseFloatOrNull(data.peso_bruto),
      peso_liquido: parseFloatOrNull(data.peso_liquido),
      largura: parseFloatOrNull(data.largura),
      altura: parseFloatOrNull(data.altura),
      comprimento: parseFloatOrNull(data.comprimento),
      categoria: data.categoria || null,
      descricao: data.descricao || null,
      imagem_url: data.imagem_url || null,
      estoquematriz: parseIntOrZero(data.estoquematriz),
      estoque2: parseIntOrZero(data.estoque2),
      estoque5: parseIntOrZero(data.estoque5),
      estoqueSP: parseIntOrZero(data.estoqueSP),
      estoqueES: parseIntOrZero(data.estoqueES),
    };
  };

  const onSubmitAdd = async (data) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/produtos`, buildPayload(data));
      toast.success('Produto adicionado.');
      setAddDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar produto');
    } finally {
      setSaving(false);
    }
  };

  const onSubmitUpdate = async (data) => {
    setSaving(true);
    try {
      // Edita por SKU: a listagem vem do Postgres e não traz o id do Mongo.
      // O endpoint grava nos DOIS bancos e preserva o vínculo com o Tiny.
      await axios.patch(`${API_URL}/produtos/by-sku/${encodeURIComponent(selectedProduct.sku)}`, buildPayload(data));
      toast.success('Produto atualizado.');
      setEditDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/produtos/${encodeURIComponent(selectedProduct.sku)}`);
      toast.success('Produto excluído.');
      setDeleteDialogOpen(false);
      setEditDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir produto');
    } finally {
      setDeleting(false);
    }
  };

  const contagens = useMemo(() => {
    const c = { all: products.length, sem_estoque: products.filter((p) => !temEstoque(p)).length };
    FILIAIS.forEach((f) => {
      c[f] = products.filter((p) => qtd(p, f) > 0).length;
    });
    return c;
  }, [products]);

  const filtrados = useMemo(() => {
    const termo = semAcento(busca.trim());
    return products.filter((p) => {
      if (filtro === 'sem_estoque' && temEstoque(p)) return false;
      if (FILIAIS.includes(filtro) && qtd(p, filtro) <= 0) return false;
      return !termo || semAcento(p.nome).includes(termo) || semAcento(p.sku).includes(termo);
    });
  }, [products, busca, filtro]);

  useEffect(() => {
    setLimite(LOTE_LISTA);
  }, [busca, filtro]);

  const comEstoque = products.length - contagens.sem_estoque;
  const valorEmEstoque = products.reduce((s, p) => s + (Number(p.preco) || 0) * Math.max(0, Number(p.estoque_total) || 0), 0);
  const figuras = loading || erroCarga
    ? ['produtos no catálogo', 'com estoque', 'sem estoque', 'em estoque, a preço de venda'].map((label) => ({ value: '—', label }))
    : [
        { value: formatNumero(products.length), label: products.length === 1 ? 'produto no catálogo' : 'produtos no catálogo' },
        { value: formatNumero(comEstoque), label: 'com estoque' },
        { value: formatNumero(contagens.sem_estoque), label: 'sem estoque' },
        { value: formatBRLCompacto(valorEmEstoque), label: 'em estoque, a preço de venda' },
      ];

  // Seções do formulário como funções de renderização (não componentes): declaradas aqui dentro
  // como componentes, remontavam a cada render e o campo perdia o foco quando a validação aparecia.
  const secaoIdsTiny = (idPrefix) => (
    <div className="rounded-lg border border-border bg-sheet2 p-4">
      <div className="space-y-3">
        <div>
          <Label className="text-base font-semibold">IDs do produto no Tiny, por filial</Label>
          <p className="mt-1 text-xs text-gray-500">
            Cadastre o ID do produto em cada filial para consultar o estoque em tempo real
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}tiny_id_matriz`} className="text-sm">Matriz</Label>
            <Input id={`${idPrefix}tiny_id_matriz`} placeholder="Ex.: 838358388" {...register('tiny_id_matriz')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}tiny_id_filial2`} className="text-sm">Filial 2</Label>
            <Input id={`${idPrefix}tiny_id_filial2`} placeholder="Ex.: 838358389" {...register('tiny_id_filial2')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}tiny_id_filial5`} className="text-sm">Filial 5</Label>
            <Input id={`${idPrefix}tiny_id_filial5`} placeholder="Ex.: 838358390" {...register('tiny_id_filial5')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}tiny_id_filialSP`} className="text-sm">Filial SP</Label>
            <Input id={`${idPrefix}tiny_id_filialSP`} placeholder="Ex.: 838358391" {...register('tiny_id_filialSP')} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor={`${idPrefix}tiny_id_filialES`} className="text-sm">Filial ES</Label>
            <Input id={`${idPrefix}tiny_id_filialES`} placeholder="Ex.: 838358392" {...register('tiny_id_filialES')} />
          </div>
        </div>
      </div>
    </div>
  );

  const secaoBasica = (idPrefix) => (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}sku`}>SKU *</Label>
          <Input
            id={`${idPrefix}sku`}
            placeholder="PROD-001"
            aria-invalid={!!errors.sku}
            className={errors.sku ? 'border-red-500' : ''}
            data-testid={`${idPrefix}input-sku`}
            {...register('sku')}
          />
          <FieldError error={errors.sku} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}unidade`}>Unidade *</Label>
          <Controller
            name="unidade"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id={`${idPrefix}unidade`} data-testid={`${idPrefix}select-unidade`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UN">Unidade (UN)</SelectItem>
                  <SelectItem value="CX">Caixa (CX)</SelectItem>
                  <SelectItem value="PC">Peça (PC)</SelectItem>
                  <SelectItem value="KG">Quilograma (KG)</SelectItem>
                  <SelectItem value="MT">Metro (MT)</SelectItem>
                  <SelectItem value="LT">Litro (LT)</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <FieldError error={errors.unidade} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}nome`}>Nome do produto *</Label>
        <Input
          id={`${idPrefix}nome`}
          placeholder="Nome completo do produto"
          aria-invalid={!!errors.nome}
          className={errors.nome ? 'border-red-500' : ''}
          data-testid={`${idPrefix}input-nome`}
          {...register('nome')}
        />
        <FieldError error={errors.nome} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}preco`}>Preço de venda (R$) *</Label>
          <Input
            id={`${idPrefix}preco`}
            type="number"
            step="0.01"
            placeholder="0,00"
            aria-invalid={!!errors.preco}
            className={errors.preco ? 'border-red-500' : ''}
            data-testid={`${idPrefix}input-preco`}
            {...register('preco')}
          />
          <FieldError error={errors.preco} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}custo_medio`}>Custo médio (R$)</Label>
          <Input
            id={`${idPrefix}custo_medio`}
            type="number"
            step="0.01"
            placeholder="0,00"
            aria-invalid={!!errors.custo_medio}
            className={errors.custo_medio ? 'border-red-500' : ''}
            data-testid={`${idPrefix}input-custo-medio`}
            {...register('custo_medio')}
          />
          <FieldError error={errors.custo_medio} />
        </div>
      </div>
    </>
  );

  const secaoEstoque = (idPrefix, title) => (
    <div className="border-t border-border pt-4">
      <h4 className="mb-3 text-sm font-semibold">{title}</h4>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[
          ['estoquematriz', 'Matriz'],
          ['estoque2', 'Filial 2'],
          ['estoque5', 'Filial 5'],
          ['estoqueSP', 'Filial SP'],
          ['estoqueES', 'Filial ES'],
        ].map(([fieldName, label]) => (
          <div key={fieldName} className="space-y-2">
            <Label htmlFor={`${idPrefix}${fieldName}`}>{label}</Label>
            <Input
              id={`${idPrefix}${fieldName}`}
              type="number"
              min="0"
              placeholder="0"
              aria-invalid={!!errors[fieldName]}
              className={errors[fieldName] ? 'border-red-500' : ''}
              data-testid={`${idPrefix}input-${fieldName}`}
              {...register(fieldName)}
            />
            <FieldError error={errors[fieldName]} />
          </div>
        ))}
      </div>
    </div>
  );

  const secaoAdicional = (idPrefix) => (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}categoria`}>Categoria</Label>
          <Input
            id={`${idPrefix}categoria`}
            placeholder="Ex.: Injetáveis, Descartáveis"
            data-testid={`${idPrefix}input-categoria`}
            {...register('categoria')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}imagem_url`}>URL da imagem</Label>
          <Input
            id={`${idPrefix}imagem_url`}
            type="url"
            placeholder="https://exemplo.com/imagem.jpg"
            aria-invalid={!!errors.imagem_url}
            className={errors.imagem_url ? 'border-red-500' : ''}
            data-testid={`${idPrefix}input-imagem-url`}
            {...register('imagem_url')}
          />
          <FieldError error={errors.imagem_url} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}descricao`}>Descrição</Label>
        <Textarea
          id={`${idPrefix}descricao`}
          placeholder="Descrição detalhada do produto"
          rows={4}
          data-testid={`${idPrefix}input-descricao`}
          {...register('descricao')}
        />
      </div>
    </>
  );

  const visiveis = filtrados.slice(0, limite);
  const restantes = filtrados.length - visiveis.length;

  return (
    <div data-testid="products-page">
      <PageCover
        title="Produtos"
        subtitle="Catálogo e estoque das cinco filiais, sincronizados com o Tiny"
        actions={
          <>
            <CoverSearch value={busca} onChange={setBusca} placeholder="Nome ou SKU" data-testid="search-products-input" />
            {canEdit && (
              <>
                <CoverButton onClick={handleSync} disabled={syncing} data-testid="sync-products-button">
                  <RefreshCw className={syncing ? 'animate-spin' : ''} />
                  {syncing ? 'Sincronizando' : 'Sincronizar'}
                </CoverButton>
                <CoverButton primary onClick={handleOpenAddDialog} data-testid="add-product-button">
                  <Plus /> Adicionar produto
                </CoverButton>
              </>
            )}
          </>
        }
        figures={figuras}
      />
      <PageBody>
        <Card className="overflow-hidden">
          <SheetHeader title={loading ? 'Carregando produtos' : erroCarga ? 'Produtos' : `${formatNumero(filtrados.length)} ${filtrados.length === 1 ? 'produto' : 'produtos'}`}>
            {canEdit && !loading && !erroCarga && filtrados.length > 0 && (
              <span className="text-[12.5px] text-gray-500">Clique no nome para editar</span>
            )}
            {!erroCarga && <WorkflowStatusFilter value={filtro} onChange={setFiltro} counts={contagens} config={FILTROS} />}
          </SheetHeader>

          {!loading && erroCarga ? (
            <div className="flex flex-wrap items-center gap-4 px-5 py-12">
              <p className="text-sm text-gray-500">Não foi possível carregar o catálogo agora.</p>
              <Button variant="outline" size="sm" onClick={recarregar}>Tentar de novo</Button>
            </div>
          ) : !loading && products.length === 0 ? (
            <p className="px-5 py-12 text-sm text-gray-500">
              {canEdit ? 'O catálogo está vazio. Use Sincronizar para trazer os produtos do Tiny.' : 'O catálogo está vazio.'}
            </p>
          ) : loading ? (
            <ul aria-hidden>
              {Array.from({ length: 8 }).map((_, i) => (
                <li key={i} className="flex items-center gap-6 border-b border-border px-5 py-3.5">
                  <span className="h-4 w-24 animate-pulse rounded bg-muted" />
                  <span className="h-4 w-80 animate-pulse rounded bg-muted" />
                  <span className="ml-auto h-4 w-40 animate-pulse rounded bg-muted" />
                </li>
              ))}
            </ul>
          ) : filtrados.length === 0 ? (
            <p className="px-5 py-12 text-sm text-gray-500">
              {busca ? `Nenhum produto encontrado para "${busca}".` : 'Nenhum produto neste filtro.'}
            </p>
          ) : (
            <>
              {/* Colunas fixas: o nome ocupa a sobra (sem vão entre ele e os números) e corta com reticências.
                  As filiais ficam sob um cabeçalho de grupo, com rótulos curtos como na tabela antiga.
                  SKU com 144px cabe o p95 real (15 caracteres); custo e margem só a partir de 1700px,
                  para o nome não encolher demais numa tela de 1920px com zoom de 125%. */}
              <Table className="min-w-[900px] table-fixed">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead rowSpan={2} className="w-[144px] align-bottom">SKU</TableHead>
                    <TableHead rowSpan={2} className="align-bottom">Produto</TableHead>
                    <TableHead rowSpan={2} className="w-[116px] align-bottom text-right min-[1800px]:w-[128px]">Preço</TableHead>
                    {canEdit && <TableHead rowSpan={2} className="hidden w-[120px] align-bottom text-right min-[1700px]:table-cell min-[1800px]:w-[128px]">Custo médio</TableHead>}
                    {canEdit && <TableHead rowSpan={2} className="hidden w-[88px] align-bottom text-right min-[1700px]:table-cell">Margem</TableHead>}
                    <TableHead colSpan={5} className="h-8 w-[320px] text-center min-[1800px]:w-[400px]">Estoque por filial</TableHead>
                    <TableHead rowSpan={2} className="w-[88px] align-bottom text-right min-[1800px]:w-[104px]">Total</TableHead>
                  </TableRow>
                  <TableRow className="hover:bg-transparent">
                    {FILIAIS.map((f) => (
                      <TableHead key={f} className="h-8 text-right first:pl-3 last:pr-3" title={f}>
                        <span aria-hidden>{ROTULO_CURTO[f]}</span>
                        <span className="sr-only">{f}</span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visiveis.map((p) => {
                    const total = Number(p.estoque_total) || 0;
                    const custo = custoDe(p);
                    const margem = margemDe(p);
                    const ids = tinyIdsTexto(p);
                    // Só o nome abre a edição (não a linha inteira): duplo clique para copiar um SKU ou
                    // um preço não abre o formulário por engano.
                    return (
                      <TableRow key={p.sku} data-testid={`product-card-${p.sku}`} className="hover:bg-sheet2">
                        <TableCell className="truncate text-[13px] tabular-nums text-gray-500" title={ids ? `${p.sku}\nNo Tiny:\n${ids}` : p.sku}>
                          {p.sku}
                        </TableCell>
                        <TableCell className="truncate font-medium">
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={(e) => { abridorRef.current = e.currentTarget; handleEdit(p); }}
                              title={p.nome}
                              data-testid={`edit-product-${p.sku}`}
                              className="max-w-full truncate rounded-sm text-left underline-offset-2 hover:underline focus-visible:underline"
                            >
                              {p.nome}
                            </button>
                          ) : (
                            <span title={p.nome}>{p.nome}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatBRL(p.preco)}</TableCell>
                        {canEdit && (
                          <TableCell className="hidden text-right tabular-nums text-gray-600 dark:text-gray-300 min-[1700px]:table-cell">
                            {custo ? formatBRL(custo) : <Traco />}
                          </TableCell>
                        )}
                        {canEdit && (
                          <TableCell className={`hidden text-right tabular-nums min-[1700px]:table-cell ${margem < 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-600 dark:text-gray-300'}`}>
                            {margem != null ? `${margem}%` : <Traco />}
                          </TableCell>
                        )}
                        {FILIAIS.map((f) => {
                          const q = qtd(p, f);
                          return (
                            <TableCell key={f} className={`text-right tabular-nums ${corQtd(q)}`}>
                              {q ? formatNumero(q) : <Traco />}
                            </TableCell>
                          );
                        })}
                        <TableCell className={`text-right tabular-nums ${total > 0 ? 'font-medium' : corQtd(total)}`}>
                          {total ? formatNumero(total) : <Traco />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {restantes > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-[12.5px] text-gray-500">
                  <span>Mostrando {formatNumero(visiveis.length)} de {formatNumero(filtrados.length)}. A busca acha qualquer um.</span>
                  <Button variant="outline" size="sm" onClick={() => setLimite((l) => l + LOTE_LISTA)}>
                    Mostrar mais {formatNumero(Math.min(LOTE_LISTA, restantes))}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </PageBody>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar produto</DialogTitle>
            <DialogDescription>Preencha as informações do produto</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmitAdd)} noValidate>
            <div className="space-y-4 py-4">
              {secaoIdsTiny('add-')}
              {secaoBasica('add-')}

              <div className="border-t border-border pt-4">
                <h4 className="mb-3 text-sm font-semibold">Dimensões e peso</h4>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  {[
                    ['peso_bruto', 'Peso bruto (kg)', '0.001'],
                    ['peso_liquido', 'Peso líquido (kg)', '0.001'],
                    ['largura', 'Largura (cm)', '0.01'],
                    ['altura', 'Altura (cm)', '0.01'],
                    ['comprimento', 'Comprimento (cm)', '0.01'],
                  ].map(([fieldName, label, step]) => (
                    <div key={fieldName} className="space-y-2">
                      <Label htmlFor={`add-${fieldName}`}>{label}</Label>
                      <Input
                        id={`add-${fieldName}`}
                        type="number"
                        step={step}
                        placeholder="0,00"
                        aria-invalid={!!errors[fieldName]}
                        className={errors[fieldName] ? 'border-red-500' : ''}
                        data-testid={`add-input-${fieldName}`}
                        {...register(fieldName)}
                      />
                      <FieldError error={errors[fieldName]} />
                    </div>
                  ))}
                </div>
              </div>

              {secaoEstoque('add-', 'Estoque inicial por filial')}
              {secaoAdicional('add-')}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-product-button">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar produto
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto"
          onCloseAutoFocus={(e) => {
            // Sem Trigger do Radix o foco cairia no <body>: volta para o nome que abriu a edição.
            if (abridorRef.current?.isConnected) {
              e.preventDefault();
              abridorRef.current.focus();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Editar produto</DialogTitle>
            <DialogDescription>{selectedProduct ? `SKU ${selectedProduct.sku}` : 'Atualize as informações do produto'}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmitUpdate)} noValidate>
            <div className="space-y-4 py-4">
              {secaoIdsTiny('edit-')}
              {secaoBasica('edit-')}
              {secaoEstoque('edit-', 'Estoque por filial')}
              {secaoAdicional('edit-')}
            </div>

            {/* Excluir fica aqui, longe da linha da tabela: um clique errado na lista não apaga nada. */}
            <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
              <Button
                ref={excluirRef}
                type="button"
                variant="ghost"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={saving}
                className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10"
                data-testid="delete-product-button"
              >
                Excluir produto
              </Button>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="update-product-button">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando
                    </>
                  ) : (
                    'Salvar alterações'
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onCloseAutoFocus={(e) => {
          // Cancelou a exclusão: o foco volta para o Excluir, dentro da edição que continua aberta.
          if (excluirRef.current?.isConnected) {
            e.preventDefault();
            excluirRef.current.focus();
          }
        }}
        title="Excluir produto"
        description="Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita."
        confirmText="Excluir produto"
        onConfirm={handleConfirmDelete}
        loading={deleting}
        variant="destructive"
        details={
          selectedProduct
            ? [
                { label: 'Nome', value: selectedProduct.nome },
                { label: 'SKU', value: selectedProduct.sku },
              ]
            : null
        }
      />
    </div>
  );
};

export default Products;
